import { salesSelectionReason } from "../salesProductSelection";
import { isInvoicedStatus } from "../salesMemoStatus";
import { isMediaReference } from "../../products/previewMedia";

/** Backend copy the phone credit note keys on (BillingMemoServiceImpl NO_SOURCE_INVOICE / OTHER_PARTY_PREFIX). */
export const NO_SOURCE_INVOICE = "No HKP invoice found for this piece";
export const OTHER_PARTY_PREFIX = "This piece belongs to another party: ";

export type ScanProblem =
  | { kind: "noInvoice" }
  | { kind: "otherParty"; party: string }
  | { kind: "text"; text: string };

export const scanProblem = (message: unknown, credit: boolean): ScanProblem => {
  const text = String(message || "");
  if (text === NO_SOURCE_INVOICE) return { kind: "noInvoice" };
  if (text.startsWith(OTHER_PARTY_PREFIX)) return { kind: "otherParty", party: text.slice(OTHER_PARTY_PREFIX.length) };
  if (text.startsWith("Already on memo") || text.startsWith("On another memo") || text.startsWith("Someone else changed this piece")) {
    return { kind: "text", text };
  }
  if (text.startsWith("Select an exact HKP ID") || text === "QR_NOT_A_PRODUCT") {
    return { kind: "text", text: "We couldn't find this piece. Check the tag, or search by name." };
  }
  if (credit) {
    const creditReasons: Record<string, string> = {
      "This piece was already credited": "This piece is already on a confirmed credit note.",
      "This piece is already on this memo": "This piece is already on this credit note.",
      "Piece ownership changed after the original sale": "The owner changed after the sale, so it can't be credited here.",
    };
    if (creditReasons[text]) return { kind: "text", text: creditReasons[text] };
  }
  return { kind: "text", text: salesSelectionReason(text, credit ? "This piece could not be added. Try again." : undefined) };
};

export type StatusTone = "draft" | "ready" | "done" | "closed";

export const phoneStatus = (status?: string, type?: string): { label: string; tone: StatusTone } => {
  if (status === "READY_FOR_BILLING") return { label: type === "CREDIT" ? "To credit" : "To bill", tone: "ready" };
  if (isInvoicedStatus(status)) return { label: type === "CREDIT" ? "Credited" : "Invoiced", tone: "done" };
  if (status === "CANCELLED") return { label: "Cancelled", tone: "closed" };
  if (status === "OUT_ON_APPROVAL") return { label: "Out on approval", tone: "ready" };
  if (status === "CLOSED") return { label: "Closed", tone: "done" };
  return { label: "Draft", tone: "draft" };
};

export const initials = (name?: string) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "?";

/** Credit lines grouped by the invoice they came from, in the order they were scanned. */
export const groupBySourceInvoice = <T extends { sourceInvoiceNumber?: string; sourceMemoCode?: string; sourceMemoId?: string }>(lines: T[]) => {
  const groups = new Map<string, { invoice: string; memoCode: string; lines: T[] }>();
  lines.forEach((line) => {
    const key = line.sourceMemoId || line.sourceInvoiceNumber || "";
    const group = groups.get(key) || { invoice: line.sourceInvoiceNumber || "", memoCode: line.sourceMemoCode || "", lines: [] };
    group.lines.push(line);
    groups.set(key, group);
  });
  return Array.from(groups.values());
};

export const shortDate = (value?: string | number | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/**
 * Props for HKPImage from a stored photo. Memo lines keep the media-service reference ("hkp-media:<id>"),
 * which no API signs on the memo; passing the id lets HKPImage fetch the right-sized copy itself.
 */
export const photoProps = (stored?: unknown, assetId?: unknown): { legacyUrl?: string; mediaId?: string } => {
  const url = String(stored || "").trim();
  const id = String(assetId || "").trim();
  if (isMediaReference(url)) return { mediaId: url.slice("hkp-media:".length) };
  if (url) return { legacyUrl: url, mediaId: id || undefined };
  return id ? { mediaId: id } : {};
};

/**
 * A piece held on some other memo can't be added, so the picker hides it. Pieces on this memo are held too;
 * `seen` keeps those (and any the user tapped here) visible so a removed piece doesn't vanish from the grid.
 */
export const heldElsewhere = (product: { productUniqueId: string; commerceState?: string }, seen: Set<string>) =>
  product.commerceState === "SELECTED_FOR_BILLING" && !seen.has(product.productUniqueId);

export const hasPhoto =(props: { legacyUrl?: string; mediaId?: string }) => Boolean(props.legacyUrl || props.mediaId);
